import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2, Play, X } from 'lucide-react';
import { api, type ReportPreview } from '../lib/api';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { EmailPreviewFrame } from './EmailPreviewFrame';
import { useCsvObjectUrl } from '../lib/csv';
import { formatNumber } from '../lib/format';

interface Props {
  reportId: string;
  reportName: string;
  onClose: () => void;
  initialDate?: string;
  autoRun?: boolean;
}

function yesterdayISO(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function PreviewModal({ reportId, reportName, onClose, initialDate, autoRun }: Props) {
  const [date, setDate] = useState<string>(initialDate ?? yesterdayISO());

  const mutation = useMutation({
    mutationFn: (vars: { reportId: string; date?: string }) =>
      api.previewReport(vars.reportId, vars.date),
  });

  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRun && !autoRanRef.current) {
      autoRanRef.current = true;
      mutation.mutate({ reportId, date });
    }
  }, [autoRun, reportId, date, mutation]);

  const csvUrl = useCsvObjectUrl(mutation.data?.csv_base64);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-4xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">Preview · {reportName}</h2>
            <p className="text-xs text-muted-foreground font-mono">{reportId}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Run options</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  mutation.mutate({ reportId, date });
                }}
              >
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Target date
                  </label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-44"
                    required
                  />
                </div>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Run preview
                    </>
                  )}
                </Button>
                <div className="text-xs text-muted-foreground">
                  Executes the Athena query and renders the email{' '}
                  <span className="font-medium">without sending</span>. May take 5–30 seconds.
                </div>
              </form>
            </CardContent>
          </Card>

          {mutation.isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Preview failed</p>
                <p className="text-xs mt-1 font-mono whitespace-pre-wrap">
                  {(mutation.error as Error).message}
                </p>
              </div>
            </div>
          )}

          {mutation.data && (
            <PreviewResult preview={mutation.data} csvUrl={csvUrl} />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewResult({
  preview,
  csvUrl,
}: {
  preview: ReportPreview;
  csvUrl: string | null;
}) {
  const empty = preview.row_count === 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Rows" value={formatNumber(preview.row_count)} emphasize={empty} />
        <Tile label="Duration" value={`${(preview.duration_ms / 1000).toFixed(1)}s`} />
        <Tile label="Target date" value={preview.target_date} />
        <Tile label="Client" value={preview.client} />
      </div>

      {empty && (
        <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Query returned 0 rows.</p>
            <p className="text-xs mt-1 opacity-90">
              The query ran but produced no results. If this was a scheduled run, recipients would
              still get an email with an empty table.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Email subject</CardTitle>
            <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
              {preview.subject}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline">{preview.csv_filename}</Badge>
            {csvUrl && (
              <a
                href={csvUrl}
                download={preview.csv_filename}
                className="inline-flex items-center gap-2 h-8 px-3 rounded-md bg-secondary text-secondary-foreground text-xs font-medium hover:bg-secondary/80 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Download CSV
              </a>
            )}
          </div>
        </CardHeader>
      </Card>

      <EmailPreviewFrame
        preview={preview}
        heightClass="h-[600px]"
        actions={
          <span className="font-mono text-xs text-muted-foreground">
            query {preview.query_execution_id.slice(0, 8)}…
          </span>
        }
      />
    </div>
  );
}

function Tile({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-lg font-bold tabular-nums ${emphasize ? 'text-warning' : ''}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
